/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiBadge,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { Tag } from '@modules/resources-shared-components/Tags/types'

import TagsDisplay from './TagsDisplay'

type Props = {
  tags: Tag[]
  onAddTagsClick?: () => void
  hasPermissionsToAddTags?: boolean
  hideAddTagsButton?: boolean
}

const TagsPanel = ({
  tags,
  onAddTagsClick,
  hasPermissionsToAddTags,
  hideAddTagsButton = false,
}: Props) => {
  const { formatMessage } = useIntl()

  return (
    <EuiPanel grow={true} hasBorder={true}>
      {/* magic number to align the tags panel with the endpoints panel */}
      <EuiFlexGroup justifyContent='spaceBetween' alignItems='center' style={{ marginTop: '-5px' }}>
        <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
          <EuiTitle size='xs'>
            <h3>
              <FormattedMessage id='resource.tags.heading' defaultMessage='Tags' />
            </h3>
          </EuiTitle>
          <EuiFlexItem grow={false}>
            <EuiBadge
              aria-label={formatMessage(
                {
                  id: 'resource.tags.count',
                  defaultMessage: 'Resource has {count} tags',
                },
                { count: tags.length },
              )}
            >
              {tags.length}
            </EuiBadge>
          </EuiFlexItem>
        </EuiFlexGroup>
        {!hideAddTagsButton && (
          <EuiButtonEmpty
            iconType='plusCircle'
            size='s'
            onClick={onAddTagsClick}
            disabled={!hasPermissionsToAddTags}
          >
            <FormattedMessage id='resource.tags.addTags' defaultMessage='Add tags' />
          </EuiButtonEmpty>
        )}
      </EuiFlexGroup>

      {tags.length !== 0 && (
        <Fragment>
          <EuiSpacer size='s' />

          <TagsDisplay tags={tags} />
        </Fragment>
      )}
    </EuiPanel>
  )
}

export default TagsPanel
